import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router'
import { AuthProvider } from './contexts/AuthContext'
import { PodcastProvider } from './contexts/PodcastContext'
import { PageFocusProvider } from './contexts/PageFocusContext'
import { IvyDockProvider } from './contexts/IvyDockContext'
import IvyDock from './components/IvyDock'
import MainLayout from './components/MainLayout'
import ProtectedRoute from './components/ProtectedRoute'
import ScopeGate from './components/access/ScopeGate'
import LoadingSpinner from './components/LoadingSpinner'
import ErrorBoundary from './components/ErrorBoundary'

const LandingPage = lazy(() => import('./pages/LandingPage'))
const AuthPage = lazy(() => import('./pages/AuthPage'))
const TeamAcceptPage = lazy(() => import('./pages/TeamAcceptPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const SignalCastPage = lazy(() => import('./pages/SignalCastPage'))
const YouTubeIntelPage = lazy(() => import('./pages/YouTubeIntelPage'))
const GuestIntelligencePage = lazy(() => import('./pages/GuestIntelligencePage'))
const TrendIntelligencePage = lazy(() => import('./pages/TrendIntelligencePage'))
const EpisodeDNAPage = lazy(() => import('./pages/EpisodeDNAPage'))
const ClipIntelligencePage = lazy(() => import('./pages/ClipIntelligencePage'))
const RealityDashboardPage = lazy(() => import('./pages/RealityDashboardPage'))
const CastBackPage = lazy(() => import('./pages/CastBackPage'))
const VoiceIdentityPage = lazy(() => import('./pages/VoiceIdentityPage'))
const MonetizationPage = lazy(() => import('./pages/MonetizationPage'))
const CompetitiveRadarPage = lazy(() => import('./pages/CompetitiveRadarPage'))
const LiveLaunchPage = lazy(() => import('./pages/LiveLaunchPage'))
const EpisodeBrainPage = lazy(() => import('./pages/EpisodeBrainPage'))
const EpisodeBrainWorkspace = lazy(() => import('./pages/EpisodeBrainWorkspace'))
const AudiencePage = lazy(() => import('./pages/AudiencePage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const IvyPage = lazy(() => import('./pages/IvyPage'))
const IntegrationPage = lazy(() => import('./pages/IntegrationPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
// W6 — admin-only Intelligence Health dashboard. Component-level admin gate
// (Garreth Q5); migrate to <AdminRouteGuard> as a follow-up once role-based
// access matures across App.tsx.
const IntelligenceHealthDashboard = lazy(() => import('./pages/admin/IntelligenceHealthDashboard'))

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PodcastProvider>
          <PageFocusProvider>
          <IvyDockProvider>
          <ErrorBoundary>
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner size="lg" /></div>}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/about" element={<AboutPage />} />

              {/* Team-member invite accept (public; page gates on config.featureTeamMembers) */}
              <Route path="/team/accept" element={<TeamAcceptPage />} />

              {/* Onboarding (protected, no MainLayout) */}
              <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

              {/* Authenticated routes (wrapped in ProtectedRoute + MainLayout) */}
              <Route path="/dashboard" element={<ProtectedRoute><MainLayout><DashboardPage /></MainLayout></ProtectedRoute>} />
              <Route path="/signalcast" element={<ProtectedRoute><MainLayout><ScopeGate scope="signalcast"><SignalCastPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/trends" element={<ProtectedRoute><MainLayout><ScopeGate scope="signalcast"><TrendIntelligencePage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/youtube" element={<ProtectedRoute><MainLayout><ScopeGate scope="youtube_analytics"><YouTubeIntelPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/guests" element={<ProtectedRoute><MainLayout><ScopeGate scope="guests"><GuestIntelligencePage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/retention" element={<ProtectedRoute><MainLayout><ScopeGate scope="retention"><EpisodeDNAPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/clips" element={<ProtectedRoute><MainLayout><ScopeGate scope="clips"><ClipIntelligencePage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/reality" element={<ProtectedRoute><MainLayout><ScopeGate scope="reality"><RealityDashboardPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/castback" element={<ProtectedRoute><MainLayout><ScopeGate scope="castback"><CastBackPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/voice" element={<ProtectedRoute><MainLayout><ScopeGate scope="voice"><VoiceIdentityPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/monetization" element={<ProtectedRoute><MainLayout><ScopeGate scope="monetization"><MonetizationPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/competitive" element={<ProtectedRoute><MainLayout><ScopeGate scope="competitive"><CompetitiveRadarPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/launch" element={<ProtectedRoute><MainLayout><ScopeGate scope="launch"><LiveLaunchPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/brain/plan/:id" element={<ProtectedRoute><MainLayout><ScopeGate scope="episodes"><EpisodeBrainWorkspace /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/brain" element={<ProtectedRoute><MainLayout><ScopeGate scope="episodes"><EpisodeBrainPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/audience" element={<ProtectedRoute><MainLayout><ScopeGate scope="dashboard"><AudiencePage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/integration" element={<ProtectedRoute><MainLayout><ScopeGate scope="platforms"><IntegrationPage /></ScopeGate></MainLayout></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><MainLayout><SettingsPage /></MainLayout></ProtectedRoute>} />
              <Route path="/settings/connections" element={<ProtectedRoute><MainLayout><SettingsPage /></MainLayout></ProtectedRoute>} />
              <Route path="/ivy" element={<ProtectedRoute><MainLayout><ScopeGate scope="episodes"><IvyPage /></ScopeGate></MainLayout></ProtectedRoute>} />

              {/* W6 — admin-only intelligence health dashboard */}
              <Route path="/admin/intelligence-health" element={<ProtectedRoute><MainLayout><IntelligenceHealthDashboard /></MainLayout></ProtectedRoute>} />

              {/* 404 catch-all */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
          <IvyDock />
          </IvyDockProvider>
          </PageFocusProvider>
        </PodcastProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
